import { Injectable, inject } from '@angular/core';
import { CombinedGraphQLErrors, type ErrorLike } from '@apollo/client/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap, take } from 'rxjs/operators';
import { ApolloClientService } from './apollo-client.service';
import { WebAnalyticsQueries } from '../graphql/queries/web-analytics.queries';
import { NexusUserQueries } from '../graphql/queries/nexus-user.queries';
import {
  WebAnalyticsActiveNowData,
  WebAnalyticsDashboardData,
  WebAnalyticsUserDetailData,
  WebAnalyticsUsersResult,
} from '../models/web-analytics-dashboard';
import {
  NexusUserProfile,
  parseNexusUserReturnValue,
} from '../utils/nexus-user-profile';

export interface WebAnalyticsDashboardFilters {
  appId: string;
  startDate: Date | null;
  endDate: Date | null;
  /** Substring match on event type (case-insensitive). */
  filterEventType?: string;
  filterUsername?: string;
  filterCountry?: string;
  filterBrowser?: string;
  filterOs?: string;
  filterDevice?: string;
  filterCity?: string;
  /** Substring match on path (case-insensitive). */
  filterPath?: string;
}

export interface WebAnalyticsUsersQueryParams {
  filters: WebAnalyticsDashboardFilters;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  limit?: number;
  offset?: number;
}

export interface WebAnalyticsUserDetailQueryParams {
  filters: WebAnalyticsDashboardFilters;
  username: string;
  eventsLimit?: number;
  eventsCursor?: string | null;
  eventsFilterEventType?: string;
}

@Injectable({ providedIn: 'root' })
export class WebAnalyticsService {
  private readonly apolloClientService = inject(ApolloClientService);

  getDashboard(
    filters: WebAnalyticsDashboardFilters,
    options?: { includePreviousPeriod?: boolean },
  ): Observable<WebAnalyticsDashboardData | null> {
    return this.runDashboardQuery(filters, options);
  }

  getAppIds(
    startDate: Date,
    endDate: Date,
  ): Observable<string[]> {
    const vars = {
      startDate: this.toYmdUtc(startDate),
      endDate: this.toYmdUtc(endDate),
    };
    return this.apolloClientService.webAnalyticsApolloClient$.pipe(
      take(1),
      switchMap((client) =>
        from(
          client.query<{ webAnalyticsAppIds: string[] }>({
            query: WebAnalyticsQueries.getAppIds,
            variables: vars,
            fetchPolicy: 'network-only',
          }),
        ).pipe(
          mergeMap((r) =>
            this.assertGraphqlQueryResult(r, r.data?.webAnalyticsAppIds).pipe(
              mergeMap((ids) => of(ids ?? [])),
            ),
          ),
        ),
      ),
    );
  }

  getActiveNow(
    appId: string,
    windowMinutes = 5,
  ): Observable<WebAnalyticsActiveNowData | null> {
    const trimmed = (appId ?? '').trim();
    return this.apolloClientService.webAnalyticsApolloClient$.pipe(
      take(1),
      switchMap((client) =>
        from(
          client.query<{ webAnalyticsActiveNow: WebAnalyticsActiveNowData }>({
            query: WebAnalyticsQueries.getActiveNow,
            variables: {
              appId: trimmed === '' ? null : trimmed,
              windowMinutes,
            },
            fetchPolicy: 'network-only',
          }),
        ).pipe(
          mergeMap((r) =>
            this.assertGraphqlQueryResult(r, r.data?.webAnalyticsActiveNow),
          ),
        ),
      ),
    );
  }

  getUsers(
    params: WebAnalyticsUsersQueryParams,
  ): Observable<WebAnalyticsUsersResult | null> {
    const f = params.filters;
    const appId = (f.appId ?? '').trim();
    if (!f.startDate || !f.endDate) {
      return throwError(() => new Error('startDate and endDate are required'));
    }
    const vars = {
      appId: appId === '' ? null : appId,
      startDate: this.toYmdUtc(f.startDate),
      endDate: this.toYmdUtc(f.endDate),
      search: this.trimOrNull(params.search),
      sortBy: this.trimOrNull(params.sortBy),
      sortDescending: params.sortDescending ?? true,
      limit: params.limit ?? 25,
      offset: params.offset ?? 0,
      ...this.filterVars(f),
    };
    return this.apolloClientService.webAnalyticsApolloClient$.pipe(
      // Client stream never completes; take(1) so inner query completes and forkJoin can finish.
      take(1),
      switchMap((client) =>
        from(
          client.query<{ webAnalyticsUsers: WebAnalyticsUsersResult }>({
            query: WebAnalyticsQueries.getUsers,
            variables: vars,
            fetchPolicy: 'network-only',
          }),
        ).pipe(
          mergeMap((r) =>
            this.assertGraphqlQueryResult(r, r.data?.webAnalyticsUsers),
          ),
        ),
      ),
    );
  }

  getUserDetail(
    params: WebAnalyticsUserDetailQueryParams,
  ): Observable<WebAnalyticsUserDetailData | null> {
    const f = params.filters;
    const appId = (f.appId ?? '').trim();
    const username = (params.username ?? '').trim();
    if (!f.startDate || !f.endDate || !username) {
      return throwError(
        () => new Error('startDate, endDate, and username are required'),
      );
    }
    const vars = {
      appId: appId === '' ? null : appId,
      username,
      startDate: this.toYmdUtc(f.startDate),
      endDate: this.toYmdUtc(f.endDate),
      eventsLimit: params.eventsLimit ?? null,
      eventsCursor: this.trimOrNull(params.eventsCursor ?? undefined),
      eventsFilterEventType: this.trimOrNull(params.eventsFilterEventType),
      ...this.filterVars(f),
    };
    return this.apolloClientService.webAnalyticsApolloClient$.pipe(
      take(1),
      switchMap((client) =>
        from(
          client.query<{ webAnalyticsUserDetail: WebAnalyticsUserDetailData }>({
            query: WebAnalyticsQueries.getUserDetail,
            variables: vars,
            fetchPolicy: 'network-only',
          }),
        ).pipe(
          mergeMap((r) =>
            this.assertGraphqlQueryResult(r, r.data?.webAnalyticsUserDetail),
          ),
        ),
      ),
    );
  }

  /** Nexus directory lookup (same AppSync endpoint as web analytics). */
  getNexusUserProfile(username: string): Observable<NexusUserProfile | null> {
    const u = (username ?? '').trim();
    if (!u) {
      return of(null);
    }
    return this.apolloClientService.webAnalyticsApolloClient$.pipe(
      take(1),
      switchMap((client) =>
        from(
          client.query<{ nexusUser: { returnValue: string | null } | null }>({
            query: NexusUserQueries.getNexusUser,
            variables: { username: u },
            fetchPolicy: 'network-only',
          }),
        ).pipe(
          mergeMap((r) => {
            if (r.error) {
              return of(null);
            }
            return of(
              parseNexusUserReturnValue(r.data?.nexusUser?.returnValue),
            );
          }),
          catchError(() => of(null)),
        ),
      ),
    );
  }

  private runDashboardQuery(
    filters: WebAnalyticsDashboardFilters,
    options?: { includePreviousPeriod?: boolean },
  ): Observable<WebAnalyticsDashboardData | null> {
    const appId = (filters.appId ?? '').trim();
    if (!filters.startDate || !filters.endDate) {
      return throwError(
        () => new Error('startDate and endDate are required'),
      );
    }
    const startDate = this.toYmdUtc(filters.startDate);
    const endDate = this.toYmdUtc(filters.endDate);
    return this.apolloClientService.webAnalyticsApolloClient$.pipe(
      take(1),
      switchMap((client) =>
        from(
          client.query<{ webAnalyticsDashboard: WebAnalyticsDashboardData }>({
            query: WebAnalyticsQueries.getDashboard,
            variables: {
              appId: appId === '' ? null : appId,
              startDate,
              endDate,
              includePreviousPeriod: options?.includePreviousPeriod ?? false,
              ...this.filterVars(filters),
            },
            fetchPolicy: 'network-only',
          }),
        ).pipe(
          mergeMap((r) =>
            this.assertGraphqlQueryResult(r, r.data?.webAnalyticsDashboard),
          ),
        ),
      ),
    );
  }

  /**
   * Surfaces AppSync/GraphQL validation errors (e.g. schema mismatch) instead of silent null + skeleton.
   * Apollo Client v4 exposes a single `error` on query results (e.g. CombinedGraphQLErrors).
   */
  private assertGraphqlQueryResult<T>(
    result: { error?: ErrorLike },
    data: T | null | undefined,
  ): Observable<T | null> {
    if (result.error) {
      const msg = CombinedGraphQLErrors.is(result.error)
        ? result.error.errors.map((e) => e.message).join(' ')
        : result.error.message;
      return throwError(() => new Error(msg));
    }
    return of(data ?? null);
  }

  private filterVars(f: WebAnalyticsDashboardFilters) {
    return {
      filterEventType: this.trimOrNull(f.filterEventType),
      filterUsername: this.trimOrNull(f.filterUsername),
      filterCountry: this.trimOrNull(f.filterCountry),
      filterBrowser: this.trimOrNull(f.filterBrowser),
      filterOs: this.trimOrNull(f.filterOs),
      filterDevice: this.trimOrNull(f.filterDevice),
      filterCity: this.trimOrNull(f.filterCity),
      filterPath: this.trimOrNull(f.filterPath),
    };
  }

  private trimOrNull(s: string | undefined): string | null {
    const t = (s ?? '').trim();
    return t === '' ? null : t;
  }

  /** Partition keys in DynamoDB use UTC calendar days (matches ingest). */
  toYmdUtc(d: Date): string {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
